import { State } from 'reducers';
import { useTranslation } from 'react-i18next';
import { getIsLoadingBuildingThemes, getBuildingThemes } from 'selectors/buildingsSelectors';
import { getBuildingThemesRequest } from 'actions/buildingActions';
import { BuildingId, DeviceType, ThemeId } from 'shared/models';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filterEligibleThemes } from './filterEligibleThemes';
import { escapeHtml } from 'util/escapeHtml';
import { sortNamesBy } from 'logic/sortNames';
import { useDeviceTypes } from "hooks";

require('./ThemeSelectField.css');

// TODO: Look at consolidating this with the FontSelectField and making a generic component

declare const $: any;

export type Props = {
    id?: string;
    label?: string;
    labelKey?: string;
    buildingId: BuildingId | null;
    deviceType: DeviceType | null;
    disabled?: boolean;
    isPI: boolean;
    value: ThemeId | null;
    onChange(themeId: ThemeId | null): void;
};

export const ThemeSelectField = ({ id, label, labelKey, buildingId, deviceType, isPI, value, disabled = false, onChange }: Props) => {
    const { t } = useTranslation();
    const selectElementRef = useRef<HTMLSelectElement | null>(null);
    const themes = useSelector((state: State) => buildingId ? getBuildingThemes(state, { buildingId }) : {});
    const isLoading = useSelector((state: State) => buildingId ? getIsLoadingBuildingThemes(state, { buildingId }) : false);
    const { deviceTypes, isLoadingDeviceTypes } = useDeviceTypes();

    // Prepare the values that will appear in the drop down menu
    const values = useMemo(() => {
        const eligibleThemes = filterEligibleThemes(themes, deviceType, isPI, isLoadingDeviceTypes ? {} : deviceTypes);
        return [
            ...eligibleThemes.map(({ id, displayName }) => ({
                value: id,
                text: escapeHtml(displayName || ''),
                name: displayName || '',
            }))
                .sort(sortNamesBy(t => t.name)),
        ];
    }, [ themes, deviceType, isPI, t, isLoadingDeviceTypes, deviceTypes ]);

    const dispatch = useDispatch();

    useEffect(() => {
        if (buildingId && !themes && !isLoading) {
            dispatch(getBuildingThemesRequest(buildingId));
        }
    }, [ buildingId, themes, isLoading ]);

    // Initialize the menu
    useEffect(() => {
        const selectElement = selectElementRef.current;
        if (selectElement) {
            $(selectElement).dropdown({
                allowAdditions: false,
                onChange(value: string) {
                    onChange(value === 'none' ? null : value);
                },
            });
        }
    }, [ selectElementRef ]);

    // Set the menu selections
    useEffect(() => {
        const selectElement = selectElementRef.current;
        if (selectElement) {
            $(selectElement).dropdown('setup menu', { values });
        }
    }, [ selectElementRef, values ]);

    // Update the Semantic UI theme selection drop down with the new value
    useEffect(() => {
        const selectElement = selectElementRef.current;
        if (selectElement) {
            $(selectElement).dropdown('set selected', value);
        }
    }, [ selectElementRef, value ]);

    if (!buildingId) {
        disabled = true;
    }

    return (
        <div className={ `field theme-select-field ${disabled ? 'disabled' : ''}` }>
            { (label || labelKey) &&
                <label
                    htmlFor={ id }
                    style={ { display: 'block' } }
                >
                    { label || t(labelKey!) }
                </label>
            }
            <select
                id={ id }
                ref={ selectElementRef }
                value={ value || "none" }
                onChange={ () => {} }
                disabled={ disabled }
            >
                <option
                    key={ "none" }
                    value={ "none" }
                >
                    { t('THEMES.NO_THEME') }
                </option>
                { values.map(theme =>
                    <option
                        key={ theme.value }
                        value={ theme.value }
                    >
                        { theme.text }
                    </option>
                ) }
            </select>
        </div>
    );
};
